import { useState } from "react";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { indiaHub } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Reusable newsletter signup strip — places on Landing, India hub bottom,
 * regulator-page bottom. The `source` prop tags the lead row so admins can
 * see which location converted. Wires to the same `/india-hub/leads`
 * endpoint that auto-subscribes the email to the India Risk Digest.
 */
export default function NewsletterSignup({ source = "newsletter_direct", className = "" }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await indiaHub.lead({
        email: email.trim(),
        source: `newsletter_direct:${source}`,
        newsletter_consent: true,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe — check the email address");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`relative overflow-hidden rounded-2xl border border-emerald-400/30 bg-gradient-to-r from-[#0D7377]/40 via-[#0D7377]/20 to-[#065F46]/30 px-5 sm:px-7 py-6 ${className}`} data-testid={`newsletter-signup-${source}`}>
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4 items-center">
        <div>
          <h3 className="font-display text-lg sm:text-xl font-bold leading-tight">India Risk Intelligence — straight to your inbox</h3>
          <p className="mt-1 text-sm text-rp-text/90">
            Join 500+ BFSI risk professionals. Every Monday. Free. Unsubscribe anytime.
          </p>
        </div>
        {done ? (
          <div className="flex items-center gap-2 text-sm text-emerald-300" data-testid={`newsletter-signup-success-${source}`}>
            <CheckCircle2 className="h-4 w-4" /> Subscribed — check your inbox to confirm.
          </div>
        ) : (
          <form onSubmit={submit} className="flex gap-2">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="bg-rp-surface/80 border-rp-border text-rp-text"
              data-testid={`newsletter-signup-input-${source}`}
            />
            <Button type="submit" disabled={busy} className="rounded-md bg-emerald-500 hover:bg-emerald-600 text-white font-semibold whitespace-nowrap" data-testid={`newsletter-signup-submit-${source}`}>
              <Mail className="h-4 w-4 mr-1" /> {busy ? "…" : "Subscribe free"} <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}
